<script setup lang="ts">
import { capitalize } from "vue";

const props = defineProps({
	type: {
		type: String,
		default: "text"
	},
	title: {
		type: String,
		default: "Title"
	},
	required: {
		type: Boolean,
		default: false
	},
	error: {
		type: String,
		default: ""
	},
	disabled: {
		type: Boolean,
		default: false
	},
	min: {
		type: Number,
		default: 0
	},
	max: {
		type: Number,
		default: 1
	},
	decimalPlaces: {
		type: Number,
		default: 2
	},
	modelValue: {
		type: [String, Number],
		default: undefined
	}
});
const emit = defineEmits(["update:modelValue"]);

const id = useId();
const isFocused = ref(false);

const modelValueAccessor = computed({
	get () {
		return props.modelValue;
	},
	set (value = "") {
		let serializedValue: number | string | undefined = value;
		if (props.type === "number") {
			const number = parseFloat(value as string);

			if (isNaN(number)) {
				serializedValue = props.min;
			} else {
				let sanitizedValue = number;
				if (number < props.min) {
					sanitizedValue = props.min;
				} else if (number > props.max) {
					sanitizedValue = props.max;
				}
				serializedValue = +(sanitizedValue.toFixed(props.decimalPlaces));
			}
		}

		emit("update:modelValue", serializedValue);
	}
});
</script>

<template>
	<div class="relative">
		<label
			:for="id"
			class="absolute top-[18px] left-[18px] bg-white text-gray-dark text-sm cursor-text select-none duration-100"
			:class="(isFocused || modelValue) && 'px-2 !text-black !-top-[10px] cursor-default'"
		>
			<span>{{ title }}</span>
			<span v-if="required">&nbsp;*</span>
		</label>
		<input
			v-bind="$attrs"
			:id="id"
			v-model="modelValueAccessor"
			size="16"
			:type="type"
			class="w-full bg-transparent p-4 border-2 border-gray text-sm focus-visible:outline-none"
			:class="isFocused && '!border-black'"
			:required="required"
			:disabled="disabled"
			@focusin="isFocused = true"
			@focusout="isFocused = false"
		/>
		<TransitionFade>
			<div
				v-if="error"
				class="text-sm mt-1"
			>
				{{ capitalize(error) }}
			</div>
		</TransitionFade>
	</div>
</template>
